import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { SyncReview, SyncReviewPage as Page, listSyncReviews } from '../../../../api/stays-sync-client';
import { listStaysBuildings } from '../../../../api/stays-client';
import type { StaysBuilding } from '../../../../interfaces/stays';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useAuth } from '../../../../context/auth-provider';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { useNotificationFrames } from '../../../notifications/use-notification-frames';
import { Staleable, StateSwitch } from '../../../../motion';
import { ListSkeleton, LoadError, skeletonHeight } from '../../dashboard/dashboard-shared-components/load-state';
import { TypeFilterSelect } from '../../dashboard/dashboard-users-components/dashboard-users.styled';
import { resolveClock } from '../../../../function-utils/building-time';
import { layout, palette, spacing, typography } from '../../../../style-utils/theme';
import { StaysSectionTabs } from '../stays-section';
import { SyncBanners } from './sync-banners';
import { SyncReviewList } from './sync-review-list';
import { Muted } from './sync.styled';

/**
 * CALENDAR CHANGES — /dashboard/stays/sync-review (plan S5, screens §2): every
 * change a connected calendar brought that waits for the host, across the
 * listings the reader can see. Filters: building (?building=), listing
 * (?listing=, from a listing's tab or a notification), "To review" / "Recent".
 * "Show more" pages with the server's cursor. Loading, failed and empty are
 * three different renders; a refetch keeps the list (Staleable).
 */

const Screen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
  padding-bottom: ${layout.fabReserve};
`;

const H2 = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
  min-width: 0;
`;

const ToolbarField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

/* The status switch (44px) centred on the 48px select beside it. */
const ToolbarControl = styled.div`
  display: flex;
  align-items: center;
  min-height: ${layout.fieldHeight};
  min-width: 0;
`;

const ToolbarLabel = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

type View = 'loading' | 'error' | 'empty' | 'ready';
type Tab = 'pending' | 'recent';

const STATE_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'pending', label: 'To review' },
  { value: 'recent', label: 'Recent' },
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const SyncReviewPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const canManage = can(permissions, PERMISSIONS.STAYS_MANAGE);
  const desk = useDeskInPlan();
  const params = new URLSearchParams(location.search);
  const listingParam = params.get('listing');
  const listingId = listingParam && UUID.test(listingParam) ? listingParam.toLowerCase() : null;
  const buildingParam = Number(params.get('building'));
  const buildingId = Number.isInteger(buildingParam) && buildingParam > 0 ? buildingParam : null;

  const [tab, setTab] = useState<Tab>('pending');
  const [buildings, setBuildings] = useState<StaysBuilding[]>([]);
  const [view, setView] = useState<View>('loading');
  const [items, setItems] = useState<SyncReview[]>([]);
  const [page, setPage] = useState<Page | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const seq = useRef(0);

  useEffect(() => {
    listStaysBuildings()
      .then(r => setBuildings(r.items))
      .catch(() => setBuildings([]));
  }, []);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (quiet) setRefreshing(true);
      else setView('loading');
      try {
        const out = await listSyncReviews({
          state: tab,
          ...(listingId ? { listing_id: listingId } : {}),
          ...(buildingId ? { building_id: buildingId } : {}),
        });
        if (mine !== seq.current) return;
        setPage(out);
        setItems(out.items);
        setNext(out.next_cursor);
        setMoreFailed(false);
        setNow(new Date());
        setView(out.items.length ? 'ready' : 'empty');
      } catch {
        if (mine !== seq.current) return;
        if (!quiet) setView('error');
      } finally {
        if (mine === seq.current) setRefreshing(false);
      }
    },
    [tab, listingId, buildingId]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useNotificationFrames(row => {
    if (row.event_type === 'stays.sync_review_needed') void load(true);
  });

  const more = async () => {
    if (!next || loadingMore) return;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const out = await listSyncReviews({
        state: tab,
        cursor: next,
        ...(listingId ? { listing_id: listingId } : {}),
        ...(buildingId ? { building_id: buildingId } : {}),
      });
      setItems(list => [...list, ...out.items.filter(r => !list.some(x => x.id === r.id))]);
      setNext(out.next_cursor);
    } catch {
      setMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const setFilter = (key: 'building' | 'listing', value: string | null) => {
    const q = new URLSearchParams(location.search);
    if (value) q.set(key, value);
    else q.delete(key);
    if (key === 'building') q.delete('listing');
    const s = q.toString();
    navigate(`/dashboard/stays/sync-review${s ? `?${s}` : ''}`);
  };

  const clock = resolveClock({ surface: 'app', response: null });
  const listingName = listingId ? (items.find(r => r.listing.id === listingId) || { listing: { name: null } }).listing.name : null;
  const can_ = canManage && (!page || page.can_manage);

  return (
    <Screen>
      <StaysSectionTabs />
      <H2>Calendar changes</H2>
      <Muted>Stays, date changes and cancellations your connected calendars found. Nothing changes until you answer.</Muted>

      <Toolbar>
        <ToolbarField>
          <ToolbarLabel htmlFor="sync-review-building">{w('Building', desk)}</ToolbarLabel>
          <TypeFilterSelect
            id="sync-review-building"
            aria-label={w('Building', desk)}
            value={buildingId ?? ''}
            onChange={e => setFilter('building', e.target.value || null)}
          >
            <option value="">{w('allBuildings', desk)}</option>
            {buildings.map(b => (
              <option key={b.building_id} value={b.building_id}>
                {b.name}
              </option>
            ))}
          </TypeFilterSelect>
        </ToolbarField>
        {listingId ? (
          <ToolbarField>
            <ToolbarLabel as="span">Listing</ToolbarLabel>
            <AppButton variant="secondary" size="compact" type="button" onClick={() => setFilter('listing', null)}>
              {`${listingName || 'One listing'} · Show all listings`}
            </AppButton>
          </ToolbarField>
        ) : null}
        <ToolbarControl>
          <SegmentedControl<Tab> label="Show changes" options={STATE_OPTIONS} value={tab} onChange={setTab} />
        </ToolbarControl>
      </Toolbar>

      {page && buildingId && tab === 'pending' ? (
        <SyncBanners
          scope="building"
          buildingId={buildingId}
          pending={0}
          failing={page.health.failing}
          guestNameRequired={page.health.guest_name_required}
          zone={clock.zone}
          now={now}
        />
      ) : null}

      <ClockNote clock={clock} />
      <StateSwitch state={view} minHeight={skeletonHeight(4, 140)}>
        {view === 'loading' ? <ListSkeleton rows={4} rowHeight={140} label="calendar changes" /> : null}
        {view === 'error' ? <LoadError message="Calendar changes could not be loaded." onRetry={() => void load(false)} /> : null}
        {view === 'empty' ? (
          <Muted role="status">
            {tab === 'pending' ? 'Every calendar change has been reviewed.' : 'No calendar change was answered in the last 14 days.'}
          </Muted>
        ) : null}
        {view === 'ready' ? (
          <Staleable stale={refreshing}>
            <SyncReviewList
              items={items}
              canManage={can_ && tab === 'pending'}
              zone={clock.zone}
              now={now}
              showListing={!listingId}
              onChanged={() => void load(true)}
            />
            {next ? (
              <div>
                <AppButton variant="secondary" type="button" loading={loadingMore} loadingLabel="Loading…" onClick={() => void more()}>
                  Show more
                </AppButton>
              </div>
            ) : null}
            {moreFailed ? <LoadError message="More changes could not be loaded." onRetry={() => void more()} /> : null}
          </Staleable>
        ) : null}
      </StateSwitch>
    </Screen>
  );
};
